import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Save, ExternalLink, Smartphone, Monitor, Copy, Store } from "lucide-react";

import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import LandingPreview from "@/components/LandingPreview";
import { useAuth } from "@/lib/auth";

import BuilderEditorPanel from "./builder/BuilderEditorPanel";
import PublishMarketplaceModal from "./builder/PublishMarketplaceModal";

export default function Builder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPro = user?.role === "admin" || user?.plan === "pro_monthly" || user?.plan === "pro_yearly";

  const [lm, setLm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [topic, setTopic] = useState("");
  const [variantTab, setVariantTab] = useState("A");
  const [customPathInput, setCustomPathInput] = useState("");
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [pubForm, setPubForm] = useState({ title: "", description: "", category: "Marketing", tags: "" });
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    api.get(`/lead-magnets/${id}`).then((r) => {
      setLm(r.data);
      setTopic(r.data.title);
      setCustomPathInput(r.data.custom_path || "");
    }).catch(() => {
      toast.error("Failed to load");
      navigate("/dashboard");
    });
  }, [id, navigate]);

  const updateLanding = (patch) => {
    setLm((prev) => {
      if (variantTab === "B" && prev.ab_enabled) {
        const base = prev.variant_b_landing_config || prev.landing_config;
        return { ...prev, variant_b_landing_config: { ...base, ...patch } };
      }
      return { ...prev, landing_config: { ...prev.landing_config, ...patch } };
    });
  };

  const updateForm = (patch) => {
    setLm((prev) => ({
      ...prev,
      landing_config: {
        ...prev.landing_config,
        form_fields: { ...(prev.landing_config?.form_fields || {}), ...patch },
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: lm.title,
        landing_config: lm.landing_config,
        require_double_optin: !!lm.require_double_optin,
        ab_enabled: !!lm.ab_enabled,
        from_name: lm.from_name || "",
        reply_to: lm.reply_to || "",
      };
      if (lm.variant_b_landing_config) payload.variant_b_landing_config = lm.variant_b_landing_config;
      const res = await api.patch(`/lead-magnets/${id}`, payload);
      setLm(res.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setSaving(true);
    try {
      await api.patch(`/lead-magnets/${id}`, { landing_config: lm.landing_config, title: lm.title });
      await api.post(`/lead-magnets/${id}/publish`);
      toast.success("Published! Public URL is live.");
      setLm((prev) => ({ ...prev, published: true }));
    } catch (e) {
      toast.error("Publish failed");
    } finally {
      setSaving(false);
    }
  };

  const aiGenerate = async () => {
    setAiLoading(true);
    try {
      const r = await api.post("/ai/generate", { kind: "landing_copy", topic });
      const d = r.data.data || {};
      if (d.headline || d.subheadline) {
        updateLanding({
          headline: d.headline || lm.landing_config.headline,
          subheadline: d.subheadline || lm.landing_config.subheadline,
          cta_text: d.cta_text || lm.landing_config.cta_text,
          badge: d.badge || lm.landing_config.badge,
          bullets: Array.isArray(d.bullets) ? d.bullets : lm.landing_config.bullets,
        });
        toast.success("AI copy generated");
      } else {
        toast.error("AI returned unexpected output");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI failed");
    } finally {
      setAiLoading(false);
    }
  };

  const saveCustomPath = async () => {
    try {
      const cp = (customPathInput || "").trim().toLowerCase();
      const r = await api.patch(`/lead-magnets/${id}`, { custom_path: cp });
      setLm(r.data);
      toast.success(cp ? "Branded URL saved" : "Custom path cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    }
  };

  const openPublishModal = () => {
    if (!isPro) { toast.error("Marketplace publishing is a Pro feature"); return; }
    if (!lm.published) { toast.error("Publish your lead magnet first (top-right button)"); return; }
    setPubForm({
      title: lm.title,
      description: lm.landing_config?.subheadline || "",
      category: "Marketing",
      tags: "",
    });
    setShowPublishModal(true);
  };

  const submitPublish = async () => {
    setPublishing(true);
    try {
      await api.post("/marketplace/publish", {
        lead_magnet_id: id,
        title: pubForm.title.trim(),
        description: pubForm.description.trim(),
        category: pubForm.category.trim() || "General",
        tags: pubForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Published to marketplace!");
      setShowPublishModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  if (!lm) return <AppShell><div className="text-zinc-500">Loading…</div></AppShell>;

  const publicUrl = lm.custom_path
    ? `${window.location.origin}/p/${lm.custom_path}`
    : `${window.location.origin}/lm/${lm.slug}`;

  // Active landing config for editing (A or B). Form fields & colors are always shared.
  const activeLanding = (variantTab === "B" && lm.ab_enabled)
    ? { ...lm.landing_config, ...(lm.variant_b_landing_config || {}) }
    : lm.landing_config;

  const previewLm = (variantTab === "B" && lm.ab_enabled)
    ? { ...lm, landing_config: { ...lm.landing_config, ...(lm.variant_b_landing_config || {}) } }
    : lm;

  const copyUrl = () => { navigator.clipboard.writeText(publicUrl); toast.success("Copied"); };

  return (
    <AppShell>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="label-cap mb-1">// Builder</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter" data-testid="builder-title">{lm.title}</h1>
          <Link to={`/delivery-editor/${id}`} className="text-sm font-bold underline mt-1 inline-block" data-testid="goto-delivery-editor">→ Edit delivery page content</Link>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={copyUrl} className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid="copy-url-btn"><Copy size={14} /> Copy URL</button>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid="preview-public-btn">
            <ExternalLink size={14} /> Public URL
          </a>
          <button onClick={save} disabled={saving} className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid="save-btn"><Save size={14} /> Save</button>
          <button onClick={openPublishModal} className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid="publish-marketplace-btn"><Store size={14} /> Publish to Marketplace</button>
          <button onClick={publish} disabled={saving} className="btn-brutal !px-3 !py-2 text-sm" data-testid="publish-btn">
            {lm.published ? "Re-publish" : "Publish"}
          </button>
        </div>
      </div>

      {/* Variant tabs */}
      {lm.ab_enabled && (
        <div className="mb-4 flex items-center gap-2">
          <span className="label-cap">Editing variant:</span>
          {["A", "B"].map((v) => (
            <button
              key={v}
              onClick={() => setVariantTab(v)}
              className={`px-4 py-2 border-2 border-black font-bold ${variantTab === v ? "bg-[#FF4F00] text-white" : "bg-white"}`}
              data-testid={`variant-tab-${v}`}
            >
              VARIANT {v}
            </button>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6">
        <BuilderEditorPanel
          lm={lm}
          isPro={isPro}
          variantTab={variantTab}
          activeLanding={activeLanding}
          topic={topic}
          setTopic={setTopic}
          setLm={setLm}
          updateLanding={updateLanding}
          updateForm={updateForm}
          aiLoading={aiLoading}
          aiGenerate={aiGenerate}
          customPathInput={customPathInput}
          setCustomPathInput={setCustomPathInput}
          saveCustomPath={saveCustomPath}
        />

        {/* Preview */}
        <div className="lg:col-span-7">
          <div className="flex items-center justify-between mb-3">
            <div className="label-cap">// Live preview {lm.ab_enabled && `· Variant ${variantTab}`}</div>
            <div className="flex gap-2">
              <button
                onClick={() => setDevice("desktop")}
                className={`px-3 py-2 border-2 border-black ${device === "desktop" ? "bg-black text-white" : "bg-white"}`}
                data-testid="preview-desktop"
              >
                <Monitor size={14} />
              </button>
              <button
                onClick={() => setDevice("mobile")}
                className={`px-3 py-2 border-2 border-black ${device === "mobile" ? "bg-black text-white" : "bg-white"}`}
                data-testid="preview-mobile"
              >
                <Smartphone size={14} />
              </button>
            </div>
          </div>
          <div className={`preview-frame mx-auto transition-all ${device === "mobile" ? "max-w-[400px]" : "w-full"}`}>
            <div className="overflow-y-auto max-h-[800px]">
              <LandingPreview lm={previewLm} />
            </div>
          </div>
        </div>
      </div>

      {showPublishModal && (
        <PublishMarketplaceModal
          form={pubForm}
          setForm={setPubForm}
          publishing={publishing}
          onCancel={() => setShowPublishModal(false)}
          onSubmit={submitPublish}
        />
      )}
    </AppShell>
  );
}
